'use client';

import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';

import { SvgIconWrapper } from 'lighthouse';
import { SearchOutlineMdV2SVG } from 'lighthouse-icons';

import { useSearchHistoryStore } from '@/stores/search/useSearchHistoryStore';

import classes from './index.module.css';
import { HeroSearchProps } from './models';

import { CloseCircle } from 'iconsax-react';

const PLACEHOLDER_PREFIX = 'جست و جوی محصول در';
const CYCLE_MS = 4000;
const ANIMATION_MS = 580;

const SearchSuggestions = dynamic(() => import('../SearchSuggestions').then((mod) => mod.SearchSuggestions), { ssr: false });

export const HeroSearch = ({ titles }: HeroSearchProps) => {
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const [leavingIndex, setLeavingIndex] = useState<number | null>(null);
    const [isFocused, setIsFocused] = useState(false);
    const activeIndexRef = useRef(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const searchRootRef = useRef<HTMLDivElement>(null);
    const mobileRootRef = useRef<HTMLDivElement>(null);
    const addHistory = useSearchHistoryStore((state) => state.addHistory);

    const hasTitles = titles.length > 0;
    const showAnimatedPlaceholder = !query && !isFocused;

    useEffect(() => {
        activeIndexRef.current = activeIndex;
    }, [activeIndex]);

    useEffect(() => {
        if (!hasTitles || !showAnimatedPlaceholder || titles.length < 2) return;

        const timer = window.setInterval(() => {
            const previousIndex = activeIndexRef.current;
            setLeavingIndex(previousIndex);
            setActiveIndex((previousIndex + 1) % titles.length);
        }, CYCLE_MS);

        return () => window.clearInterval(timer);
    }, [hasTitles, showAnimatedPlaceholder, titles.length]);

    useEffect(() => {
        if (leavingIndex === null) return;

        const timer = window.setTimeout(() => {
            setLeavingIndex(null);
        }, ANIMATION_MS);

        return () => window.clearTimeout(timer);
    }, [leavingIndex]);

    useEffect(() => {
        if (!isFocused) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isFocused]);

    useEffect(() => {
        if (!isFocused) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsFocused(false);
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isFocused]);

    useEffect(() => {
        if (!isFocused) return;

        const handleFocusIn = (event: FocusEvent) => {
            const target = event.target as Node | null;
            if (!target) return;
            if (searchRootRef.current?.contains(target)) return;
            if (mobileRootRef.current?.contains(target)) return;
            setIsFocused(false);
        };

        document.addEventListener('focusin', handleFocusIn);
        return () => document.removeEventListener('focusin', handleFocusIn);
    }, [isFocused]);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setQuery(event.target.value);
    };

    const handleSearchSubmit = () => {
        addHistory(query);
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        handleSearchSubmit();
    };

    const handleFillQuery = (value: string) => {
        setQuery(value);
        addHistory(value);
    };

    const handleProductSelect = (value: string) => {
        addHistory(value);
    };

    const handleClose = () => setIsFocused(false);

    const handleClearQuery = () => {
        setQuery('');
    };

    const appRoot = typeof document !== 'undefined' ? document.getElementById('app') : null;
    const showClearButton = !!query;

    return (
        <>
            {isFocused ? (
                <button
                    type='button'
                    aria-label='بستن پیشنهادهای جستجو'
                    className='fixed inset-0 z-[100] hidden border-0 bg-[rgba(16,24,40,0.2)] backdrop-blur-[2px] md:block'
                    onClick={handleClose}
                />
            ) : null}
            <div ref={searchRootRef} className={`relative z-20 w-full max-w-md ${isFocused ? 'md:z-[110]' : ''}`.trim()}>
                <div className='relative z-[1]'>
                    <form
                        onSubmit={handleSubmit}
                        className='relative flex min-h-12 w-full max-w-md items-center overflow-hidden rounded-lg border
                            border-[rgba(226,232,240,0.95)] bg-white/90 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_rgba(0,143,255,0.08)]
                            backdrop-blur-[10px]'>
                        <SvgIconWrapper
                            icon={SearchOutlineMdV2SVG}
                            size='md'
                            color='blackMediumEmphasis'
                            className='pointer-events-none absolute right-3.5 z-[2]'
                        />
                        <input
                            ref={inputRef}
                            type='search'
                            value={query}
                            onChange={handleChange}
                            onFocus={() => setIsFocused(true)}
                            className={`${classes.searchInput} relative z-[1] h-12 w-full cursor-text border-0 bg-transparent py-0 pr-[46px] text-sm
                                leading-6 text-[#101828] outline-none ${showClearButton ? 'pl-11' : 'pl-3.5'}`}
                            aria-label='جست و جوی محصول'
                        />
                        {showClearButton ? (
                            <button
                                type='button'
                                aria-label='پاک کردن جستجو'
                                className='absolute left-2.5 z-[2] inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border-0
                                    bg-transparent text-[#98a2b3] transition-colors hover:text-[#475467]'
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={handleClearQuery}>
                                <CloseCircle size={18} color='currentColor' variant='Bold' />
                            </button>
                        ) : null}
                        {showAnimatedPlaceholder && (
                            <div
                                className='pointer-events-none absolute inset-y-0 left-3.5 right-[46px] z-0 flex items-center gap-1 overflow-hidden
                                    whitespace-nowrap text-sm leading-6 text-[#98a2b3]'
                                aria-hidden>
                                <span className='shrink-0'>{PLACEHOLDER_PREFIX}</span>
                                {hasTitles ? (
                                    <span className='relative inline-block h-6 min-w-0 max-w-full overflow-hidden align-middle'>
                                        {leavingIndex !== null && (
                                            <span className={`${classes.placeholderTitle} ${classes.titleExit}`}>{titles[leavingIndex]}</span>
                                        )}
                                        <span
                                            key={titles[activeIndex]}
                                            className={`${classes.placeholderTitle}${leavingIndex !== null ? ` ${classes.titleEnter}` : ''}`}>
                                            {titles[activeIndex]}
                                        </span>
                                    </span>
                                ) : (
                                    <span className='font-semibold text-[#475467]'>خدمات ازکی‌سرویس</span>
                                )}
                            </div>
                        )}
                    </form>
                    {isFocused ? (
                        <SearchSuggestions
                            query={query}
                            onQueryChange={setQuery}
                            onFillQuery={handleFillQuery}
                            onProductSelect={handleProductSelect}
                            onSearchSubmit={handleSearchSubmit}
                            onClose={handleClose}
                            mobilePortalTarget={appRoot}
                            mobileRootRef={mobileRootRef}
                        />
                    ) : null}
                </div>
            </div>
        </>
    );
};
